/**
 * Canonical discount policy — single source of truth for every
 * customer-facing discount rule. Copy in messages/*.json must stay
 * consistent with these values; content never invents its own numbers.
 *
 * All percentages are whole numbers (e.g. 15 = 15%).
 */

export const MAX_STACKED_DISCOUNT_PCT = 25;

/** Recurring frequency ladder (automatic, per visit). */
export const FREQUENCY_DISCOUNTS = [
  { frequency: 'monthly', pct: 5 },
  { frequency: 'bi-weekly', pct: 10 },
  { frequency: 'weekly', pct: 15 },
] as const;

/**
 * Subscription commitment tiers (contract / longer-term commitment).
 * These are the values quoted on the homepage, the pricing page and the FAQ.
 *
 * The `term` keys match the `contract_discounts` keys stored in the admin
 * pricing config, so the admin editor and the calculation engine always
 * speak the same vocabulary.
 */
export const SUBSCRIPTION_DISCOUNTS = [
  { term: '3-month', months: 3, pct: 5 },
  { term: '6-month', months: 6, pct: 10 },
  { term: '12-month', months: 12, pct: 15 },
] as const;

export type SubscriptionTerm = (typeof SUBSCRIPTION_DISCOUNTS)[number]['term'];

/** Contract terms understood by the pricing engine (a term carries no discount). */
export const CONTRACT_TERMS = ['one-time', '3-month', '6-month', '12-month'] as const;
export type ContractTerm = (typeof CONTRACT_TERMS)[number];

/** First booking: 10% off, capped at €50. Applied once, via support/booking. */
export const FIRST_BOOKING_DISCOUNT_PCT = 10;
export const FIRST_BOOKING_DISCOUNT_MAX_EUR = 50;

/** Referral: €20 credit to both sides after the referred friend's 3rd clean. */
export const REFERRAL_CREDIT_EUR = 20;
export const REFERRAL_PAYOUT_AFTER_CLEAN_COUNT = 3;

/** Volume tiers for recurring business billing. */
export const VOLUME_TIERS = [
  { minMonthlyEur: 800, pct: 5 },
  { minMonthlyEur: 1500, pct: 8, businessOnly: true },
] as const;

/**
 * Number of months covered by a contract term. `one-time` covers a single
 * visit (1 month of billing), every subscription term covers its own span.
 */
export function contractMonths(term: string | undefined | null): number {
  if (!term || term === 'one-time') {
    return 1;
  }
  const match = SUBSCRIPTION_DISCOUNTS.find(t => t.term === term);
  return match ? match.months : 1;
}

/**
 * Discount percentage (as a fraction, e.g. 0.05) for a subscription term.
 * Unknown terms return 0 so an unrecognised contract never discounts by accident.
 */
export function subscriptionDiscountFraction(term: string | undefined | null): number {
  if (!term) {
    return 0;
  }
  const match = SUBSCRIPTION_DISCOUNTS.find(t => t.term === term);
  return match ? match.pct / 100 : 0;
}

/**
 * The canonical `contract_discounts` map (term -> discount fraction) used by
 * the admin pricing editor. Derived from SUBSCRIPTION_DISCOUNTS so the editor
 * can never drift from the values quoted in the customer-facing copy.
 */
export const CONTRACT_DISCOUNTS: Record<string, number> = {
  'one-time': 0,
  ...(Object.fromEntries(SUBSCRIPTION_DISCOUNTS.map(t => [t.term, t.pct / 100])) as Record<
    string,
    number
  >),
};

/**
 * Combine several discount percentages additively, capped at
 * MAX_STACKED_DISCOUNT_PCT so stacked promos can never breach margin.
 */
export function capStackedDiscountPercent(parts: number[]): number {
  const total = parts.reduce((sum, pct) => sum + Math.max(0, pct), 0);
  return Math.min(total, MAX_STACKED_DISCOUNT_PCT);
}

/** First-booking discount in euros for a given pre-discount total. */
export function firstBookingDiscountEur(preDiscountTotalEur: number): number {
  if (!(preDiscountTotalEur > 0)) {
    return 0;
  }
  const raw = (preDiscountTotalEur * FIRST_BOOKING_DISCOUNT_PCT) / 100;
  return Math.min(raw, FIRST_BOOKING_DISCOUNT_MAX_EUR);
}
